import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { goiApi, docToken, luuToken, theoDoiToken, docCongTy, luuCongTy, theoDoiCongTy, LoiApi } from '../lib/api';

export interface NguoiDung {
  id: string;
  name: string;
  email: string;
  username: string;
  roleId: 'boss' | 'ads' | 'ke_toan' | string;
  roleLabel: string;
  roleNote: string | null;
}

export type NhomQuyen = 'nav' | 'act' | 'data' | 'ai' | 'adm';
export type BangQuyen = Record<NhomQuyen, Record<string, boolean>>;

export interface DuLieuMe {
  ok: true;
  nguoiDung: NguoiDung;
  quyen: BangQuyen;
  menu: { id: string; label: string }[];
  soManHinh: number;
  congTy: ThongTinCongTy;
}

/** Nhiều công ty (BOSS 29/09/2026). */
export interface ThongTinCongTy {
  hienTai: string | null;
  duocXemTatCa: boolean;
  danhSach: { id: string; ten: string; roleId: string; roleLabel: string }[];
}

interface GiaTriPhien {
  dangTai: boolean;
  daDangNhap: boolean;
  nguoiDung: NguoiDung | null;
  quyen: BangQuyen;
  /** `co('nav', 'guardrail')` — công tắc này có đang bật cho tôi không. */
  co: (nhom: NhomQuyen, khoa: string) => boolean;
  dangXuat: () => void;
  congTy: ThongTinCongTy;
  /** Đổi công ty đang làm việc — nạp lại toàn bộ dữ liệu theo công ty mới. */
  doiCongTy: (id: string | null) => void;
}

const CONG_TY_RONG: ThongTinCongTy = { hienTai: null, duocXemTatCa: false, danhSach: [] };

const QUYEN_RONG: BangQuyen = { nav: {}, act: {}, data: {}, ai: {}, adm: {} };

const BoiCanh = createContext<GiaTriPhien | null>(null);

export function NhaCungCapPhien({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Đăng ký nghe thay đổi token, để đăng nhập xong là giao diện đổi ngay.
  const token = useSyncExternalStore(theoDoiToken, docToken, () => null);
  const coToken = Boolean(token);
  const congTyChon = useSyncExternalStore(theoDoiCongTy, docCongTy, () => null);

  const { data, isPending } = useQuery({
    // Đưa token vào khoá để đổi tài khoản là nạp lại hồ sơ, không dùng nhầm
    // dữ liệu của người đăng nhập trước.
    queryKey: ['me', token, congTyChon],
    queryFn: async () => {
      try {
        return await goiApi<DuLieuMe>('/me');
      } catch (loi) {
        // Công ty đã lưu không còn hợp lệ (bị gỡ khỏi công ty, công ty ngừng):
        // bỏ lựa chọn cũ để máy chủ chọn mặc định, thay vì kẹt 403 mọi màn.
        if (loi instanceof LoiApi && loi.status === 403 && docCongTy()) {
          luuCongTy(null);
          return goiApi<DuLieuMe>('/me');
        }
        throw loi;
      }
    },
    enabled: coToken,
    retry: false,
    staleTime: 60_000,
  });

  const giaTri = useMemo<GiaTriPhien>(() => {
    const quyen = data?.quyen ?? QUYEN_RONG;
    return {
      dangTai: coToken && isPending,
      daDangNhap: Boolean(data?.nguoiDung),
      nguoiDung: data?.nguoiDung ?? null,
      quyen,
      co: (nhom, khoa) => quyen[nhom]?.[khoa] === true,
      dangXuat: () => {
        // Báo backend thu hồi phiên trước, kẻo ai đã chép được token vẫn dùng
        // tiếp tới lúc hết hạn (8 giờ, hoặc 30 ngày nếu "Ghi nhớ tôi"). Gọi
        // không được (mất mạng, phiên đã bị thu hồi sẵn) thì vẫn xoá ở máy —
        // người dùng bấm Đăng xuất là phải ra khỏi màn hình ngay.
        const xoaTaiMay = () => {
          luuToken(null);
          luuCongTy(null);
          queryClient.clear();
        };
        if (!docToken()) {
          xoaTaiMay();
          return;
        }
        goiApi('/auth/dang-xuat', { method: 'POST', signal: AbortSignal.timeout(5000) })
          .catch(() => undefined)
          .finally(xoaTaiMay);
      },
      congTy: data?.congTy ?? CONG_TY_RONG,
      doiCongTy: (id) => {
        luuCongTy(id);
        // Xoá sạch dữ liệu đang giữ của công ty cũ rồi nạp lại theo công ty mới.
        void queryClient.resetQueries();
      },
    };
  }, [coToken, data, isPending, queryClient]);

  return <BoiCanh.Provider value={giaTri}>{children}</BoiCanh.Provider>;
}

export function dungPhien(): GiaTriPhien {
  const v = useContext(BoiCanh);
  if (!v) throw new Error('dungPhien() phải nằm trong <NhaCungCapPhien>');
  return v;
}
